import { useMemo } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

interface ReturnDistributionProps {
  returns: number[];
  height?: number;
}

interface BinDatum {
  binCenter: number;
  density: number;
  normal: number;
  label: string;
}

const normalPdf = (x: number, mean: number, std: number): number => {
  const z = (x - mean) / std;
  return Math.exp(-0.5 * z * z) / (std * Math.sqrt(2 * Math.PI));
};

export default function ReturnDistribution({
  returns,
  height = 300
}: ReturnDistributionProps) {
  const { data, meanBps, stdBps } = useMemo(() => {
    if (returns.length < 2) return { data: [], meanBps: 0, stdBps: 0 };

    const n = returns.length;
    const mean = returns.reduce((s, r) => s + r, 0) / n;
    const variance = returns.reduce((s, r) => s + (r - mean) ** 2, 0) / (n - 1);
    const std = Math.sqrt(variance);

    if (std === 0) return { data: [], meanBps: mean * 10000, stdBps: 0 };

    const min = Math.min(...returns);
    const max = Math.max(...returns);
    const numBins = Math.min(30, Math.max(10, Math.ceil(Math.sqrt(n))));
    const binWidth = (max - min) / numBins;

    const counts = new Array(numBins).fill(0);
    for (const r of returns) {
      const idx = Math.min(Math.floor((r - min) / binWidth), numBins - 1);
      counts[idx]++;
    }

    const bins: BinDatum[] = counts.map((count, i) => {
      const binCenter = min + (i + 0.5) * binWidth;
      const density = count / (n * binWidth);
      return {
        binCenter,
        density,
        normal: normalPdf(binCenter, mean, std),
        label: `${(binCenter * 100).toFixed(2)}%`
      };
    });

    return {
      data: bins,
      meanBps: mean * 10000,
      stdBps: std * 10000
    };
  }, [returns]);

  if (returns.length < 2) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-md p-6">
        <h3 className="text-sm font-medium text-gray-400 mb-4">Return Distribution</h3>
        <p className="text-gray-500 text-sm">Insufficient data for distribution analysis.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
      <h3 className="text-sm font-medium text-gray-400 mb-3">Return Distribution</h3>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis
            dataKey="label"
            tick={{ fill: '#6b7280', fontSize: 10 }}
            interval={Math.max(0, Math.floor(data.length / 6) - 1)}
            axisLine={{ stroke: '#374151' }}
            tickLine={{ stroke: '#374151' }}
          />
          <YAxis
            tick={{ fill: '#6b7280', fontSize: 10 }}
            axisLine={{ stroke: '#374151' }}
            tickLine={{ stroke: '#374151' }}
            tickFormatter={(v: number) => v.toFixed(0)}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#111827',
              border: '1px solid #374151',
              borderRadius: '6px',
              fontSize: '12px'
            }}
            formatter={(value: number, name: string) => [
              value.toFixed(2),
              name === 'density' ? 'Empirical' : 'Normal'
            ]}
            labelFormatter={(label: string) => `Return: ${label}`}
          />
          <Bar
            dataKey="density"
            fill="#3b82f6"
            fillOpacity={0.6}
            barSize={undefined}
          />
          <Line
            dataKey="normal"
            stroke="#f97316"
            strokeWidth={2}
            dot={false}
            type="monotone"
          />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-center gap-6 mt-2 text-xs text-gray-500">
        <span>Mean: <span className="text-gray-300 tabular-nums">{meanBps.toFixed(1)} bps/day</span></span>
        <span>Std Dev: <span className="text-gray-300 tabular-nums">{stdBps.toFixed(1)} bps/day</span></span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-2 bg-blue-500/60 rounded-sm inline-block" />Empirical
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-0.5 bg-orange-500 inline-block" />Normal
        </span>
      </div>
    </div>
  );
}
